import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import { StackView } from '@play-co/timestep-core/ui';
import { animDuration, isSplashScreenVisible } from 'src/lib/utils';
import { assertNever } from 'src/replicant/utils';
import { isTransitioningScene } from './SceneManager';
import PetScreen from '../screens/PetScreen';
import Screen from '../screens/Screen';
import ResponsiveScene from '../scenes/ResponsiveScene';
import DecorationScreen from '../screens/DecorationScreen';
import MinigameScreen from '../screens/MinigameScreen';
import StateObserver from 'src/StateObserver';
import { setHudVisibility } from 'src/state/ui';
import Header from '../components/Header';
import Footer from '../components/Footer';

type Screens = {
  pet: PetScreen;
  decoration: DecorationScreen;
  minigame: MinigameScreen;
};
export type ScreenId = keyof Screens;

export default class ScreenManager {
  public get enteredScreen() {
    return this.currentScreen;
  }
  public get enteringScreen() {
    return this.nextScreen;
  }

  public readonly screens: Partial<Screens> = {};
  private rootView: StackView;
  private currentScreen: ScreenId | null = null;
  private nextScreen: ScreenId | null = null;

  public readonly header: Header;
  public readonly footer: Footer;

  constructor(opts: { rootView: StackView }) {
    this.rootView = opts.rootView;

    // create hud
    this.header = new Header({ superview: this.rootView });
    this.footer = new Footer({ superview: this.rootView });
  }

  private getOrCreateScreen<ScreenName extends ScreenId>(
    screenId: ScreenName,
  ): Screens[ScreenName] {
    if (!this.screens[screenId]) {
      switch (screenId) {
        case 'pet':
          this.screens.pet = new PetScreen();
          break;

        case 'decoration':
          this.screens.decoration = new DecorationScreen();
          break;

        case 'minigame':
          this.screens.minigame = new MinigameScreen();
          break;

        default: {
          assertNever(screenId);
        }
      }
    }

    return this.screens[screenId] as Screens[ScreenName];
  }

  public async showScreen<
    ScreenName extends ScreenId,
    Args extends Parameters<Screens[ScreenName]['showScreen']>,
  >(name: ScreenName, ...args: Args): Promise<Screens[ScreenName] | null> {
    if (!name) return null;

    // Keep track of current screen.
    const previousScreen = this.screens[this.currentScreen!];
    const oldScene = previousScreen?.currentScene;

    // Invoke pre-transition lifecycle handler on new scene
    const newScreen = this.getOrCreateScreen<ScreenName>(name);

    // Preload screen resources
    await newScreen.preloadScreen(args[0] as never);

    // By default hide the HUD and have the screens decide what to show
    StateObserver.dispatch(setHudVisibility({ header: false, footer: false }));

    // Show screen
    newScreen.showScreen(args[0] as never);
    if (newScreen.currentScene instanceof ResponsiveScene) {
      newScreen.currentScene.prepareScene();
      newScreen.currentScene.onBeforeTransition();
    }

    // Remember current screen and show it
    const previousScreenName = this.currentScreen;
    this.nextScreen = name;

    // Emit events.
    newScreen.emit('ScreenShowing');
    previousScreen?.emit('ScreenHiding');

    await this.fadeAnimation(previousScreen!, newScreen);

    // Invoke scene transition lifecycle method for old screen
    const { rootView } = this;
    if (oldScene && isTransitioningScene(oldScene)) {
      oldScene.hideScene();
    }

    // update stack
    rootView.pop(true);
    rootView.push(newScreen, true);

    // Emit events.
    newScreen.emit('ScreenShown');
    previousScreen?.emit('ScreenHidden');

    this.currentScreen = name;
    this.nextScreen = null;
    // Should not delete pet screen.
    if (previousScreenName && previousScreenName !== 'pet') {
      delete this.screens[previousScreenName];
    }

    return newScreen;
  }

  private async fadeAnimation(fromScreen: Screen<any>, toScreen: Screen<any>) {
    // Attach new screen to enable cross-fade
    const { rootView } = this;
    rootView.addSubview(toScreen);

    // The new screen initial setup must happen BEFORE awaiting for loading
    // or otherwise we'll be seeing the screen while loading,
    // in other words, flickering.
    toScreen.updateOpts({
      visible: false,
      opacity: 0,
    });

    // Wait for assets loading
    await toScreen.loadAssets();

    // Invoke scene transition lifecycle method for newly visible screen
    const newScene = toScreen.currentScene;
    if (newScene && isTransitioningScene(newScene)) {
      newScene.showScene(rootView);
    }

    // Don't animate transition when loading initial screen, or while the
    // splash still covers the game and the cross-fade cannot be seen.
    const duration =
      fromScreen && !isSplashScreenVisible() ? animDuration * 2 : 0;

    // Fade screens in/out sequentially if they are the same to avoid race conditions.
    if (fromScreen === toScreen) {
      await this.fadeOut(fromScreen, duration);
      await this.fadeIn(toScreen, duration);
    }
    // Cross-fade screens
    else {
      await Promise.all([
        this.fadeOut(fromScreen, duration),
        this.fadeIn(toScreen, duration),
      ]);
    }
  }

  // ===================================================================
  // Screen transition animations
  // ===================================================================

  private async fadeIn(scene: View, duration: number) {
    if (!scene) {
      return;
    }

    scene.show();
    await animate(scene)
      .clear()
      .then({ opacity: 1, x: 0 }, duration, animate.easeInOut)
      .toPromise();
  }

  private async fadeOut(scene: View, duration: number) {
    if (!scene) {
      return;
    }

    await animate(scene)
      .clear()
      .then({ opacity: 0, x: 0 }, duration, animate.easeInOut)
      .then(() => scene.hide())
      .toPromise();
  }
}
